import React, { useState, useEffect } from 'react';
import DateRangePicker from '../components/DateRangePicker';
import useSortableData from '../hooks/useSortableData';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import Loader from "../components/Loader/Loader";
import apiRequester from "../utils/apiClient";

function EmailDateSummary() {
    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(new Date());
    const [DatewiseGroup, setDateGroup] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [templateData, setTemplateData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState('');
    const PageNo = [5, 10, 20, 50];

    const getTableColumns = () => {
        if (!templateData.length) return [];
        return Object.keys(templateData[0]);
    };

    const tableColumns = getTableColumns();

    const handleShow = async () => {
        try {
            if (!fromDate || !toDate) {
                setError('Please select both dates');
                return;
            }

            setLoading(true);
            setError('');

            const formatDate = (date) => {
                if (!date) return "";

                const d = new Date(date);
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");

                return `${year}-${month}-${day}`;
            };

            const queryParams = new URLSearchParams({
                FromDate: formatDate(fromDate),
                ToDate: formatDate(toDate),
                IsDate: DatewiseGroup ? 'true' : 'false',
            });

            const response = await apiRequester('GET', `Report/GetEmailDateSubmitted?${queryParams.toString()}`);
            const responseData = Array.isArray(response)
                ? response
                : response?.data || response?.result || response?.items || [];

            const normalizedData = (responseData || []).map((item) => {
                if (item && typeof item === 'object') {
                    return Object.keys(item).reduce((acc, key) => {
                        acc[key] = item[key] ?? '';
                        return acc;
                    }, {});
                }
                return {};
            });

            setTemplateData(normalizedData);

            // ConvertIntoRows(response.data);

        } catch (err) {
            setError(err?.message || 'Failed to fetch data');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const safePageSize = Number(itemsPerPage) > 0 ? Number(itemsPerPage) : 10;

    const handlePageSizeChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(Math.min(Math.max(page, 1), totalPages));
    };

    const filteredData = templateData.filter((item) => {
        const search = searchText.toLowerCase();

        return Object.values(item || {}).some((value) => {
            return String(value ?? '').toLowerCase().includes(search);
        });
    });

    const { sortedData, sortConfig, requestSort } = useSortableData(filteredData);

    const totalPages = Math.max(1, Math.ceil(sortedData.length / safePageSize));

    const paginatedData = sortedData.slice(
        (currentPage - 1) * safePageSize,
        currentPage * safePageSize
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [templateData, searchText, itemsPerPage]);

    const escapeCSV = (value) => {
        if (value === null || value === undefined) return "";

        const str = value.toString();

        // Escape quotes by doubling them
        const escaped = str.replace(/"/g, '""');

        // Wrap if contains special characters
        if (str.includes(",") || str.includes("\n") || str.includes('"')) {
            return `"${escaped}"`;
        }

        return escaped;
    };

    const downloadCSV = () => {
        const data = filteredData.length ? filteredData : templateData;

        if (!data.length) {
            alert("No data available to download");
            return;
        }

        const headers = tableColumns.length ? tableColumns : ["Error Code", "Error Description", "Failed Count"];

        const rows = data.map(item => {
            return headers.map((key) => escapeCSV(item[key]));
        });

        const csvContent = [
            headers.join(","),
            ...rows.map(row => row.join(","))
        ].join("\n");

        const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;"
        });

        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "EmailDateSummary.csv");

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="min-h-screen bg-slate-100 text-slate-900">
            <div className="flex min-h-screen">
                <Sidebar />

                <div className="flex-1">
                    <Topbar />

                    {loading && <Loader />}

                    <main className="p-6 md:p-8">
                        <div className="rounded-lg border border-slate-200 bg-white/95 px-4 py-4 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] backdrop-blur-sm">

                            <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(250px,300px)_1fr] md:items-end">

                                {/* Row 1 - Date Range */}
                                <div className="w-60">
                                    <label className="flex flex-col gap-2">
                                        <span className="text-xs font-medium text-slate-600">
                                            Date Range
                                        </span>

                                        <DateRangePicker
                                            value={{ fromDate, toDate }}
                                            onChange={({ fromDate: f, toDate: t }) => {
                                                setFromDate(f);
                                                setToDate(t);
                                            }}
                                            placeholder="DD-MM-YYYY ~ DD-MM-YYYY"
                                            displayFormat="DD-MM-YYYY"
                                            classNames="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                                            showFooter={true}
                                            configs={{
                                                footer: {
                                                    cancel: 'Cancel',
                                                    apply: 'Apply'
                                                }
                                            }}
                                        />
                                    </label>
                                </div>

                                <label className="flex items-center gap-2 whitespace-nowrap mb-3">
                                    <input
                                        type="checkbox"
                                        checked={DatewiseGroup}
                                        onChange={(e) => setDateGroup(e.target.checked)}
                                        className="h-4 w-4"
                                    />

                                    <span className="text-xs font-medium text-slate-600">
                                        Group By Date
                                    </span>
                                </label>

                                {/* Empty space in Row 1 */}
                                <div className="flex justify-start gap-2 md:justify-end">
                                    <button
                                        type="button"
                                        onClick={handleShow}
                                        disabled={loading}
                                        className="rounded bg-slate-700 px-6 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        {loading ? 'Loading...' : 'Show'}
                                    </button>

                                    <button
                                        type="button"
                                        className="rounded bg-slate-700 px-6 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
                                        onClick={() => {
                                            setFromDate(new Date());
                                            setToDate(new Date());
                                            setTemplateData([]);
                                            setError('');
                                        }}
                                    >
                                        Reset
                                    </button>
                                </div>



                            </div>

                            {error && (
                                <p className="mt-4 text-sm text-red-600">
                                    {error}
                                </p>
                            )}
                        </div>
                        {paginatedData.length > 0 && (
                            <div className="border border-slate-200 bg-white/95 px-2 sm:px-4 py-4 sm:py-6 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] backdrop-blur-sm rounded-lg">

                                <div className="mb-3 flex items-center gap-4 justify-between">
                                    <div className="flex items-center gap-2">
                                        <select
                                            value={itemsPerPage}
                                            onChange={handlePageSizeChange}
                                            className="w-[80px] border border-slate-200 bg-slate-50 px-3 py-2 text-sm rounded"
                                        >
                                            {PageNo.map((pageNo) => (
                                                <option key={pageNo} value={pageNo}>
                                                    {pageNo}
                                                </option>
                                            ))}
                                        </select>

                                        <span className="text-sm text-slate-600">
                                            TOTAL RECORD : {templateData.length}
                                        </span>
                                    </div>
                                    <input
                                        type="text"
                                        value={searchText}
                                        onChange={handleSearchChange}
                                        placeholder="Search Here"
                                        className="text-sm w-200 border border-slate-200 bg-slate-50 px-3 py-2 rounded focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                                    />
                                </div>

                                {/* scroll wrapper */}
                                <div className="overflow-auto max-h-[180px] mt-3">
                                    <table className="w-full min-w-[600px] table-fixed border border-slate-200 bg-white text-xs">
                                        <thead className="bg-slate-100 sticky top-0 z-10">
                                            <tr>
                                                <th className="border px-1 py-2 w-[50px]">S.No</th>
                                                {tableColumns.map((column) => (
                                                    <th
                                                        key={column}
                                                        onClick={() => requestSort(column)}
                                                        className="border px-1 py-2 cursor-pointer select-none"
                                                    >
                                                        {column}{sortConfig.key === column ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓') : ''}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>

                                        <tbody key={currentPage}>
                                            {paginatedData.map((item, index) => (
                                                <tr key={index} className="hover:bg-slate-50">
                                                    <td className="border px-1 py-1 text-center">
                                                        {(currentPage - 1) * itemsPerPage + index + 1}
                                                    </td>

                                                    {tableColumns.map((column) => (
                                                        <td
                                                            key={column}
                                                            className={`border px-1 py-1 ${column === 'MAILSUBJECT'
                                                                ? 'text-left'
                                                                : 'text-center'
                                                                }`}
                                                        >
                                                            {item[column]}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>


                                <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
                                    {/* Simple Pagination */}
                                    <div className="flex items-center gap-3 ">

                                        {/* Prev */}
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1 border rounded disabled:opacity-50"
                                        >
                                            ←
                                        </button>

                                        {/* Current Page */}
                                        <span className="px-3 py-1 border rounded bg-cyan-500 text-white">
                                            {currentPage}
                                        </span>

                                        {/* Next */}
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1 border rounded disabled:opacity-50"
                                        >
                                            →
                                        </button>

                                        <span className="text-sm text-slate-600">
                                            {currentPage} of {totalPages}
                                        </span>

                                    </div>
                                    <button
                                        onClick={downloadCSV}
                                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
                                    >
                                        Download CSV
                                    </button>
                                </div>
                            </div>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
}

export default EmailDateSummary;